import {otsBaseApi} from "../ots-api";
import {removeUndefined} from "../../util/util";
import {LocationContext} from "../../model/location/location-context";

const ALL_TAG_TYPES = [
  "PersonLocationContexts",
  "TrailerLocationContexts",
  "VehicleLocationContexts"
] as const;

type TagType = typeof ALL_TAG_TYPES[number];

const taggedBaseApi = otsBaseApi.enhanceEndpoints({
  addTagTypes: ALL_TAG_TYPES
});

const locationContextApi = taggedBaseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllCurrentLocations: builder.query<LocationContext[], {
      regionId: string | undefined,
      entityType: "PERSON" | "TRAILER" | "VEHICLE"
    }>({
      query: (arg) => ({
        url: `/locations/current`,
        method: "GET",
        params: removeUndefined({
          regionId: arg.regionId,
          entityType: arg.entityType,
          maxAge: 'PT12H',
        })
      }),
      providesTags: (result, error, arg) => {
        let type: TagType
        if (arg.entityType === "PERSON") {
          type = 'PersonLocationContexts'
        } else if (arg.entityType === "TRAILER") {
          type = 'TrailerLocationContexts'
        } else {
          type = 'VehicleLocationContexts'
        }

        if (result) {
          return [
            ...result.map(({entityId}) => ({type: type, entityId} as const)),
            {type: type, id: "LIST"}
          ];
        } else {
          return [{type: type, id: "LIST"}];
        }
      }
    }),
    getAllCurrentPersonLocations: builder.query<LocationContext[], string | undefined>({
      query: (regionId) => ({
        url: `/locations/current`,
        method: "GET",
        params: removeUndefined({
          regionId: regionId,
          entityType: 'PERSON',
          maxAge: 'PT12H',
        })
      }),
      providesTags: (result) => {
        if (result) {
          return [
            ...result.map(({entityId}) => ({type: "PersonLocationContexts", entityId} as const)),
            {type: "PersonLocationContexts", id: "LIST"}
          ];
        } else {
          return [{type: "PersonLocationContexts", id: "LIST"}];
        }
      }
    }),
    getAllCurrentTrailerLocations: builder.query<LocationContext[], string | undefined>({
      query: (regionId) => ({
        url: `/locations/current`,
        method: "GET",
        params: removeUndefined({
          regionId: regionId,
          entityType: 'TRAILER',
          maxAge: 'PT12H',
        })
      }),
      providesTags: (result) => {
        if (result) {
          return [
            ...result.map(({entityId}) => ({type: "TrailerLocationContexts", entityId} as const)),
            {type: "TrailerLocationContexts", id: "LIST"}
          ];
        } else {
          return [{type: "TrailerLocationContexts", id: "LIST"}];
        }
      }
    }),
    getAllCurrentVehicleLocations: builder.query<LocationContext[], string | undefined>({
      query: (regionId) => ({
        url: `/locations/current`,
        method: "GET",
        params: removeUndefined({
          regionId: regionId,
          entityType: 'VEHICLE',
          maxAge: 'PT12H',
        })
      }),
      providesTags: (result) => {
        if (result) {
          return [
            ...result.map(({entityId}) => ({type: "VehicleLocationContexts", entityId} as const)),
            {type: "VehicleLocationContexts", id: "LIST"}
          ];
        } else {
          return [{type: "VehicleLocationContexts", id: "LIST"}];
        }
      }
    }),
    getCurrentPersonLocation: builder.query<LocationContext, { id: string, tenantCode?: string }>({
      query: (args) => ({
        url: `/locations/current/PERSON/${args.id}`,
        method: "GET",
        params: removeUndefined({
          tenantCode: args.tenantCode,
        })
      }),
      providesTags: (result, error, arg) => [{type: "PersonLocationContexts", id: arg.id}]
    }),
    getCurrentTrailerLocation: builder.query<LocationContext, { id: string, tenantCode?: string }>({
      query: (args) => ({
        url: `/locations/current/TRAILER/${args.id}`,
        method: "GET",
        params: removeUndefined({
          tenantCode: args.tenantCode,
        })
      }),
      providesTags: (result, error, arg) => [{type: "TrailerLocationContexts", id: arg.id}]
    }),
    getCurrentVehicleLocation: builder.query<LocationContext, { id: string, tenantCode?: string }>({
      query: (args) => ({
        url: `/locations/current/VEHICLE/${args.id}`,
        method: "GET",
        params: removeUndefined({
          tenantCode: args.tenantCode,
        })
      }),
      providesTags: (result, error, arg) => [{type: "VehicleLocationContexts", id: arg.id}]
    }),
  })
});

export const {
  useGetAllCurrentLocationsQuery,
  useGetAllCurrentPersonLocationsQuery,
  useGetAllCurrentTrailerLocationsQuery,
  useGetAllCurrentVehicleLocationsQuery,
  useGetCurrentPersonLocationQuery,
  useGetCurrentTrailerLocationQuery,
  useGetCurrentVehicleLocationQuery,
} = locationContextApi;
